import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";

const KIND_LABELS = {
  strategy: { fr: "Stratégie", en: "Strategy" },
  plan: { fr: "Plan de comm", en: "Comms plan" },
  press_release: { fr: "Communiqué de presse", en: "Press release" },
  internal_memo: { fr: "Comm interne", en: "Internal memo" },
};
const STATUS_LABELS = {
  draft: { fr: "Brouillon", en: "Draft" },
  scheduled: { fr: "Programmé", en: "Scheduled" },
  published: { fr: "Publié", en: "Published" },
  archived: { fr: "Archivé", en: "Archived" },
};
const STATUS_COLOR = {
  draft: "#9a8d7a",
  scheduled: "#c98a3a",
  published: "#4f6b3a",
  archived: "#7a6f60",
};

const emptyItem = () => ({
  kind: "plan", title: "", summary: "", body_md: "",
  channels: [], scheduled_at: "", status: "draft", owner: "", target_audience: "",
});

export default function CommunicationTab({ locale = "fr" }) {
  const isFr = locale === "fr";
  const [data, setData] = useState({ items: [], counts: {}, status_counts: {}, kinds: [], statuses: [], channels: [] });
  const [filterKind, setFilterKind] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const q = new URLSearchParams();
    if (filterKind) q.set("kind", filterKind);
    if (filterStatus) q.set("status", filterStatus);
    const r = await api.get(`/admin/comm/items?${q.toString()}`);
    setData(r.data);
  }, [filterKind, filterStatus]);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async () => {
    if (!editing.title.trim()) return;
    if (editing.id) await api.patch(`/admin/comm/items/${editing.id}`, editing);
    else await api.post("/admin/comm/items", editing);
    setEditing(null);
    load();
  }, [editing, load]);
  const remove = useCallback(async (id) => {
    if (!window.confirm(isFr ? "Supprimer cet élément ?" : "Delete this item?")) return;
    await api.delete(`/admin/comm/items/${id}`);
    load();
  }, [isFr, load]);

  const [broadcasting, setBroadcasting] = useState(null); // item being broadcast

  const broadcast = useCallback((item) => {
    setBroadcasting(item);
  }, []);

  return (
    <div className="space-y-6" data-testid="communication-tab">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-2xl">{isFr ? "Communication & presse" : "Communication & press"}</h2>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <select className="input-classic" value={filterKind} onChange={(e) => setFilterKind(e.target.value)} data-testid="comm-filter-kind">
            <option value="">{isFr ? "Tous types" : "All kinds"}</option>
            {data.kinds.map((k) => <option key={k} value={k}>{KIND_LABELS[k]?.[locale] || k}</option>)}
          </select>
          <select className="input-classic" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="comm-filter-status">
            <option value="">{isFr ? "Tous statuts" : "All statuses"}</option>
            {data.statuses.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]?.[locale] || s}</option>)}
          </select>
          <button className="btn btn-primary" onClick={() => setEditing(emptyItem())} data-testid="comm-add-btn">
            {isFr ? "+ Nouveau" : "+ New"}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3">
        {data.kinds.map((k) => (
          <div key={k} className="admin-card text-center">
            <div className="font-caps text-[0.6rem]" style={{ color: "var(--muted)", letterSpacing: "0.18em" }}>
              {KIND_LABELS[k]?.[locale] || k}
            </div>
            <div className="font-display text-3xl" style={{ color: "var(--gold)" }}>{data.counts?.[k] || 0}</div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        {data.items.length === 0 && (
          <p className="font-body" style={{ color: "var(--muted)" }}>
            {isFr ? "Aucun élément. Créez votre premier communiqué." : "No items yet. Create your first."}
          </p>
        )}
        {data.items.map((it) => (
          <div key={it.id} className="admin-card" data-testid={`comm-item-${it.id}`}>
            <div className="flex flex-wrap items-start gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em", color: "var(--gold)" }}>
                    {KIND_LABELS[it.kind]?.[locale] || it.kind}
                  </span>
                  <span className="px-2 py-0.5 text-[0.65rem] rounded-sm font-caps"
                        style={{ backgroundColor: STATUS_COLOR[it.status] + "22", color: STATUS_COLOR[it.status], letterSpacing: "0.15em" }}>
                    {STATUS_LABELS[it.status]?.[locale] || it.status}
                  </span>
                  {it.scheduled_at && (
                    <span className="text-[0.7rem]" style={{ color: "var(--muted)" }}>⏱ {it.scheduled_at.slice(0, 16).replace("T", " ")}</span>
                  )}
                </div>
                <h3 className="font-display text-lg">{it.title}</h3>
                {it.summary && <p className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>{it.summary}</p>}
                {it.channels?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {it.channels.map((c) => (
                      <span key={c} className="text-[0.6rem] px-2 py-0.5 rounded-sm font-caps"
                            style={{ backgroundColor: "var(--surface-2)", letterSpacing: "0.12em" }}>{c}</span>
                    ))}
                  </div>
                )}
                {it.owner && <p className="text-[0.7rem] mt-1" style={{ color: "var(--muted)" }}>◯ {it.owner}</p>}
              </div>
              <div className="flex gap-2">
                <button className="btn btn-outline btn-sm"
                        onClick={() => broadcast(it)}
                        data-testid={`comm-broadcast-${it.id}`}>
                  ✉ {isFr ? "Envoyer" : "Send"}
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => setEditing(it)} data-testid={`comm-edit-${it.id}`}>✎</button>
                <button className="btn btn-outline btn-sm" onClick={() => remove(it.id)} data-testid={`comm-delete-${it.id}`}>✕</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <CommEditor item={editing} setItem={setEditing} onSave={save} onClose={() => setEditing(null)} channels={data.channels} isFr={isFr} />
      )}

      {broadcasting && (
        <BroadcastDialog item={broadcasting} isFr={isFr}
                          onClose={() => setBroadcasting(null)}
                          onDone={() => { setBroadcasting(null); load(); }} />
      )}
    </div>
  );
}

function CommEditor({ item, setItem, onSave, onClose, channels, isFr }) {
  const update = (k, v) => setItem({ ...item, [k]: v });
  const toggleChannel = (c) => {
    const set = new Set(item.channels || []);
    set.has(c) ? set.delete(c) : set.add(c);
    update("channels", [...set]);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(20,16,12,0.7)" }}>
      <div className="admin-card max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-4" data-testid="comm-editor">
        <h3 className="font-display text-xl">{item.id ? (isFr ? "Modifier" : "Edit") : (isFr ? "Nouveau communiqué" : "New item")}</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Type" : "Kind"}</span>
            <select className="input-classic w-full" value={item.kind} onChange={(e) => update("kind", e.target.value)} data-testid="comm-input-kind">
              {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l[isFr ? "fr" : "en"]}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Statut" : "Status"}</span>
            <select className="input-classic w-full" value={item.status} onChange={(e) => update("status", e.target.value)} data-testid="comm-input-status">
              {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l[isFr ? "fr" : "en"]}</option>)}
            </select>
          </label>
        </div>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Titre" : "Title"}</span>
          <input className="input-classic w-full" value={item.title} onChange={(e) => update("title", e.target.value)} data-testid="comm-input-title" />
        </label>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Résumé" : "Summary"}</span>
          <input className="input-classic w-full" value={item.summary} onChange={(e) => update("summary", e.target.value)} data-testid="comm-input-summary" />
        </label>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Contenu (Markdown)" : "Content (Markdown)"}</span>
          <textarea rows={6} className="input-classic w-full font-mono text-xs" value={item.body_md} onChange={(e) => update("body_md", e.target.value)} data-testid="comm-input-body" />
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Date programmée" : "Scheduled at"}</span>
            <input type="datetime-local" className="input-classic w-full" value={item.scheduled_at?.slice(0, 16) || ""} onChange={(e) => update("scheduled_at", e.target.value)} data-testid="comm-input-scheduled" />
          </label>
          <label className="block">
            <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Responsable" : "Owner"}</span>
            <input className="input-classic w-full" value={item.owner} onChange={(e) => update("owner", e.target.value)} data-testid="comm-input-owner" />
          </label>
        </div>
        <label className="block">
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Audience cible" : "Target audience"}</span>
          <input className="input-classic w-full" value={item.target_audience} onChange={(e) => update("target_audience", e.target.value)} data-testid="comm-input-audience" />
        </label>
        <div>
          <span className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.18em" }}>{isFr ? "Canaux" : "Channels"}</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {channels.map((c) => {
              const active = item.channels?.includes(c);
              return (
                <button key={c} type="button" onClick={() => toggleChannel(c)}
                        className="px-3 py-1 text-xs rounded-sm font-caps"
                        data-testid={`comm-channel-${c}`}
                        style={{ letterSpacing: "0.15em",
                                 backgroundColor: active ? "var(--cinnabar)" : "var(--surface-2)",
                                 color: active ? "white" : "var(--ink)" }}>
                  {c}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex gap-2 pt-2">
          <button className="btn btn-primary" onClick={onSave} data-testid="comm-save-btn">{isFr ? "Enregistrer" : "Save"}</button>
          <button className="btn btn-outline btn-sm" onClick={onClose} data-testid="comm-cancel-btn">{isFr ? "Annuler" : "Cancel"}</button>
        </div>
      </div>
    </div>
  );
}

function BroadcastDialog({ item, isFr, onClose, onDone }) {
  const [mode, setMode] = useState("test"); // test | all | role | function
  const [testEmail, setTestEmail] = useState("");
  const [role, setRole] = useState("benevole");
  const [func, setFunc] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [hrData, setHrData] = useState({ role_counts: {}, function_counts: {}, role_labels: {}, function_labels: {} });

  useEffect(() => {
    api.get("/admin/hr/overview").then((r) => setHrData(r.data)).catch(() => {});
  }, []);

  const send = async () => {
    setBusy(true); setResult(null);
    const payload = {};
    if (mode === "test") payload.test_email = testEmail;
    else if (mode === "all") payload.target = "all_members";
    else if (mode === "role") { payload.target = "by_role"; payload.role = role; }
    else if (mode === "function") { payload.target = "by_function"; payload.function = func; }
    try {
      const r = await api.post(`/admin/comm/items/${item.id}/broadcast`, payload);
      setResult(r.data);
    } catch (e) {
      setResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const closeAndRefresh = () => { onDone(); };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ backgroundColor: "rgba(20,16,12,0.7)" }} data-testid="broadcast-dialog">
      <div className="admin-card max-w-lg w-full space-y-4">
        <h3 className="font-display text-xl">
          {isFr ? "Diffuser :" : "Broadcast:"} <em>{item.title}</em>
        </h3>

        <div className="grid grid-cols-2 gap-2">
          {[
            { id: "test", label: isFr ? "Test (1 email)" : "Test (1 email)" },
            { id: "all", label: isFr ? "Tous les membres" : "All members" },
            { id: "role", label: isFr ? "Par rôle" : "By role" },
            { id: "function", label: isFr ? "Par fonction" : "By function" },
          ].map((opt) => (
            <button key={opt.id} type="button"
                    onClick={() => setMode(opt.id)}
                    data-testid={`broadcast-mode-${opt.id}`}
                    className="px-3 py-2 text-xs font-caps"
                    style={{ letterSpacing: "0.15em",
                              border: "1px solid var(--border)",
                              backgroundColor: mode === opt.id ? "var(--cinnabar)" : "white",
                              color: mode === opt.id ? "white" : "var(--ink)" }}>
              {opt.label}
            </button>
          ))}
        </div>

        {mode === "test" && (
          <input type="email" className="input-classic w-full" placeholder="ex: test@example.com"
                 value={testEmail} onChange={(e) => setTestEmail(e.target.value)}
                 data-testid="broadcast-test-email" />
        )}
        {mode === "all" && (
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>
            {isFr ? `${(hrData.members || []).length} membres actifs recevront ce message.`
                  : `${(hrData.members || []).length} active members will receive this.`}
          </p>
        )}
        {mode === "role" && (
          <select className="input-classic w-full" value={role} onChange={(e) => setRole(e.target.value)}
                  data-testid="broadcast-role">
            {Object.entries(hrData.role_counts).map(([r, n]) => r === "admin" ? null : (
              <option key={r} value={r}>{(hrData.role_labels?.[r] || r)} ({n})</option>
            ))}
          </select>
        )}
        {mode === "function" && (
          <select className="input-classic w-full" value={func} onChange={(e) => setFunc(e.target.value)}
                  data-testid="broadcast-function">
            <option value="">{isFr ? "— Choisir une fonction —" : "— Choose a function —"}</option>
            {Object.entries(hrData.function_counts).sort().map(([f, n]) => (
              <option key={f} value={f}>{(hrData.function_labels?.[f] || f)} ({n})</option>
            ))}
          </select>
        )}

        {result && !result.error && (
          <div className="p-3" style={{ backgroundColor: "rgba(83,128,72,0.12)", border: "1px solid var(--moss)" }}
               data-testid="broadcast-result">
            ✓ {result.sent}/{result.total} {isFr ? "envoyés" : "sent"}
            {result.failed > 0 && (
              <span style={{ color: "var(--cinnabar)" }}> · {result.failed} {isFr ? "échecs" : "failed"}</span>
            )}
          </div>
        )}
        {result?.error && (
          <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{result.error}</p>
        )}

        <div className="flex gap-2 pt-2">
          {!result && (
            <button className="btn btn-primary" onClick={send} disabled={busy}
                    data-testid="broadcast-send">
              {busy ? (isFr ? "Envoi…" : "Sending…") : (isFr ? "Envoyer" : "Send")}
            </button>
          )}
          {result && (
            <button className="btn btn-primary" onClick={closeAndRefresh} data-testid="broadcast-done">
              {isFr ? "Fermer" : "Close"}
            </button>
          )}
          {!result && (
            <button className="btn btn-outline btn-sm" onClick={onClose} data-testid="broadcast-cancel">
              {isFr ? "Annuler" : "Cancel"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

